import { ChevronDown, Fingerprint } from 'lucide-react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'
import { type EntityField, type FormValues, humanizeIdentifier } from './entity-form'

/** A field's problem, already in words: a local check or the server's reason. */
export type FieldMessages = Record<string, string>

interface FieldsProps {
  fields: EntityField[]
  values: FormValues
  errors: FieldMessages
  onChange: (name: string, value: string | boolean | undefined) => void
}

const INPUT_TYPE: Partial<Record<EntityField['kind'], string>> = {
  date: 'date',
  datetime: 'datetime-local',
}

/** The type's fields: name, required and unique up front, the rest folded away. */
export function EntityPropertyFields({ fields, values, errors, onChange }: FieldsProps) {
  const { t } = useTranslation()
  const primary = fields.filter(f => f.primary)
  const optional = fields.filter(f => !f.primary)
  const optionalHasError = optional.some(f => errors[f.name])

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="grid min-w-0 gap-3 sm:grid-cols-2">
        {primary.map(field => (
          <PropertyField
            key={field.name}
            field={field}
            value={values[field.name]}
            error={errors[field.name]}
            onChange={onChange}
          />
        ))}
      </div>
      {optional.length > 0 && (
        <Collapsible defaultOpen={optionalHasError} key={String(optionalHasError)}>
          <CollapsibleTrigger className="group flex items-center gap-1.5 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronDown
              className="size-4 transition-transform group-data-[state=open]:rotate-180"
              aria-hidden
            />
            {t('manualGraph.createEntity.moreProperties', { count: optional.length })}
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <div className="grid min-w-0 gap-3 sm:grid-cols-2">
              {optional.map(field => (
                <PropertyField
                  key={field.name}
                  field={field}
                  value={values[field.name]}
                  error={errors[field.name]}
                  onChange={onChange}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}

interface PropertyFieldProps {
  field: EntityField
  value: string | boolean | undefined
  error?: string
  onChange: FieldsProps['onChange']
}

function PropertyField({ field, value, error, onChange }: PropertyFieldProps) {
  const { t } = useTranslation()
  const id = useId()
  const hintId = `${id}-hint`
  const hint = error ?? field.description
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', field.isDisplayName && 'sm:col-span-2')}>
      <Label htmlFor={id} className="flex items-center gap-1.5">
        {humanizeIdentifier(field.name)}
        {field.required && (
          <span className="text-muted-foreground" aria-hidden>
            *
          </span>
        )}
        {field.unique && (
          <Fingerprint
            className="size-3.5 text-muted-foreground"
            aria-label={t('manualGraph.createEntity.uniqueHint')}
          />
        )}
      </Label>
      <FieldControl
        id={id}
        field={field}
        value={value}
        invalid={Boolean(error)}
        describedBy={hint ? hintId : undefined}
        onChange={onChange}
      />
      {hint && (
        <p
          id={hintId}
          className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}
        >
          {hint}
        </p>
      )}
    </div>
  )
}

interface FieldControlProps {
  id: string
  field: EntityField
  value: string | boolean | undefined
  invalid: boolean
  describedBy?: string
  onChange: FieldsProps['onChange']
}

function FieldControl({ id, field, value, invalid, describedBy, onChange }: FieldControlProps) {
  const { t } = useTranslation()
  if (field.kind === 'boolean') {
    const current = value === true ? 'yes' : value === false ? 'no' : 'unset'
    return (
      <Select
        value={current}
        onValueChange={next => onChange(field.name, next === 'unset' ? undefined : next === 'yes')}
      >
        <SelectTrigger id={id} className="w-full" aria-describedby={describedBy}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="z-[250]">
          <SelectItem value="unset">{t('manualGraph.createEntity.notSet')}</SelectItem>
          <SelectItem value="yes">{t('manualGraph.createEntity.yes')}</SelectItem>
          <SelectItem value="no">{t('manualGraph.createEntity.no')}</SelectItem>
        </SelectContent>
      </Select>
    )
  }
  const numeric = field.kind === 'integer' || field.kind === 'number'
  return (
    <Input
      id={id}
      type={INPUT_TYPE[field.kind] ?? 'text'}
      inputMode={numeric ? (field.kind === 'integer' ? 'numeric' : 'decimal') : undefined}
      value={typeof value === 'string' ? value : ''}
      onChange={event => onChange(field.name, event.target.value)}
      aria-invalid={invalid || undefined}
      aria-required={field.required || undefined}
      aria-describedby={describedBy}
      autoFocus={field.isDisplayName}
      className={cn(field.isDisplayName && 'h-10 text-base')}
    />
  )
}
